import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { validatePrioritizationCriteria } from '../../api/onboarding';

export interface OnboardingPrioritizationValidatorProps {
  prioritizationText: string;
  disabled?: boolean;
  onValidityChange: (isValid: boolean) => void;
  onValidatedMessage: (message: string, prioritizationTrimmed: string) => void;
}

type DisplayState = 'idle' | 'checking' | 'valid' | 'invalid';

const DEBOUNCE_MS = 650;

export const OnboardingPrioritizationValidator: React.FC<
  OnboardingPrioritizationValidatorProps
> = ({
  prioritizationText,
  disabled = false,
  onValidityChange,
  onValidatedMessage
}) => {
  const [display, setDisplay] = useState<DisplayState>('idle');
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>();

  useEffect(() => {
    if (disabled) {
      return;
    }

    const trimmed = prioritizationText.trim();
    if (!trimmed) {
      setDisplay('idle');
      setInvalidMessage(undefined);
      onValidityChange(false);
      return;
    }

    setDisplay('idle');
    setInvalidMessage(undefined);
    onValidityChange(false);

    const ac = new AbortController();
    const timer = window.setTimeout(() => {
      void (async () => {
        setDisplay('checking');
        try {
          const message = await validatePrioritizationCriteria(
            trimmed,
            ac.signal
          );
          if (ac.signal.aborted) return;
          onValidatedMessage(message, trimmed);
          setDisplay('valid');
          onValidityChange(true);
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          const isAbort = e instanceof Error && e.name === 'AbortError';
          if (isAbort) return;
          setDisplay('invalid');
          const msg =
            e instanceof Error ? e.message : 'Something went wrong. Try again.';
          setInvalidMessage(msg);
          onValidityChange(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [prioritizationText, disabled, onValidityChange, onValidatedMessage]);

  return (
    <Box sx={{ mt: 0.5, mb: 1.5 }} aria-live="polite">
      {display === 'idle' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <Box sx={{ width: 18, height: 18, flexShrink: 0 }} aria-hidden />
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: 'transparent',
              userSelect: 'none',
              caretColor: 'transparent'
            }}
            aria-hidden
          >
            {'\u00A0'}
          </Typography>
        </Box>
      )}
      {display === 'checking' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking prioritization…
          </Typography>
        </Box>
      )}
      {display === 'valid' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 18,
              color: 'success.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="success.main" component="span">
            Prioritization is valid.
          </Typography>
        </Box>
      )}
      {display === 'invalid' && invalidMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 18,
              color: 'error.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="error" component="span">
            {invalidMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OnboardingPrioritizationValidator;
